import { AlertCircle, Plus } from "lucide-react";
import React, { useCallback, useEffect, useState } from "react";
import { useAuth } from "../contexts/AuthContext";
import { getUserAlerts, UserAlert } from "../services/api";
import AlertForm from "./AlertForm";
import AlertsList from "./AlertsList";
import { Button } from "./ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "./ui/card";
import { Tabs, TabsContent } from "./ui/tabs";
import { useToast } from "./ui/use-toast";

const AlertsPage: React.FC = () => {
  const { isAuthenticated, user } = useAuth();
  const [alerts, setAlerts] = useState<UserAlert[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<string>("list");
  const [editingAlert, setEditingAlert] = useState<UserAlert | null>(null);
  const { toast } = useToast();

  // Función para cargar las alertas
  const fetchAlerts = useCallback(async () => {
    try {
      setLoading(true);
      const userAlerts = await getUserAlerts();
      setAlerts(userAlerts);
      setError(null);
    } catch (err) {
      console.error("Error al cargar las alertas:", err);
      setError(
        "No se pudieron cargar las alertas. Por favor, inténtalo de nuevo más tarde."
      );
      toast({
        title: "Error",
        description: "No se pudieron cargar las alertas",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  // Cargar las alertas del usuario al montar el componente
  useEffect(() => {
    if (isAuthenticated) {
      fetchAlerts();
    }
  }, [isAuthenticated, fetchAlerts]);

  // Función para manejar la creación exitosa de una alerta
  const handleAlertCreated = () => {
    fetchAlerts();
    setActiveTab("list");
    toast({
      title: "Alerta creada",
      description: "La alerta ha sido creada correctamente",
      variant: "default",
    });
  };

  // Función para manejar la actualización exitosa de una alerta
  const handleAlertUpdated = () => {
    fetchAlerts();
    setActiveTab("list");
    setEditingAlert(null);
    toast({
      title: "Alerta actualizada",
      description: "La alerta ha sido actualizada correctamente",
      variant: "default",
    });
  };

  // Función para editar una alerta
  const handleEditAlert = (alert: UserAlert) => {
    setEditingAlert(alert);
    setActiveTab("form");
  };

  // Función para cancelar la edición
  const handleCancelEdit = () => {
    setEditingAlert(null);
    setActiveTab("list");
  };

  // Si el usuario no está autenticado, mostrar un mensaje
  if (!isAuthenticated) {
    return (
      <Card className="w-full max-w-4xl mx-auto mt-8">
        <CardHeader>
          <CardTitle>Alertas Personalizadas</CardTitle>
          <CardDescription>
            Debes iniciar sesión para acceder a esta funcionalidad
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center p-6">
            <AlertCircle className="h-10 w-10 text-yellow-500 mr-2" />
            <p>
              Por favor, inicia sesión para ver y gestionar tus alertas
              personalizadas.
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="container mx-auto py-6">
      <Card className="w-full">
        <CardHeader>
          <div className="flex justify-between items-center">
            <div>
              <CardTitle className="text-2xl">Alertas Personalizadas</CardTitle>
              <CardDescription>
                Configura alertas para recibir notificaciones sobre nuevos
                documentos del BOE que coincidan con tus criterios
              </CardDescription>
            </div>
            {activeTab === "list" && (
              <Button onClick={() => setActiveTab("form")}>
                <Plus className="h-4 w-4 mr-2" />
                Nueva Alerta
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <h2 className="text-xl font-semibold mb-2">Mis Alertas</h2>
            <TabsContent value="list">
              <AlertsList
                alerts={alerts}
                loading={loading}
                error={error}
                onRefresh={fetchAlerts}
                onEdit={handleEditAlert}
              />
            </TabsContent>
            <TabsContent value="form">
              <AlertForm
                username={user?.email || ""}
                alert={editingAlert}
                onSuccess={
                  editingAlert ? handleAlertUpdated : handleAlertCreated
                }
                onCancel={handleCancelEdit}
              />
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    </div>
  );
};

export default AlertsPage;
